import { LineChart, StackedAreaChart } from "@get-dx/d3-charts";
import { numberToSpelling } from "../../../shared/utils";
import { chartTooltipHtml } from "../../chartTooltipHtml";
import { generateComparisonValues, workflowDiffOptionsMap } from "./helpers";
import { getDistributionColors } from "../../chartColors";

/**
 * Workflow Chart Renderers - Pure functions for rendering D3 charts
 * Extracted from snapshotWorkflows.js for reusability and testability
 */

/**
 * Create a LineChart with benchmark line
 * @param {Object} attrs - Chart attributes for LineChart constructor
 * @returns {Object} LineChart instance
 */
export function createLineChartWithBenchmark(attrs) {
  const chart = new LineChart(attrs);
  return chart;
}

/**
 * Render workflow average value trend chart
 * @param {Object} params - Chart parameters
 * @param {Object} params.workflow - Workflow data
 * @param {Object} params.trendData - Trend data with values array
 * @param {string} params.elChartId - DOM element ID for chart container
 * @param {string} params.xStart - Start date for x-axis
 * @param {string} params.compareTo - Comparison benchmark type
 * @returns {Object|null} LineChart instance or null if prerequisites missing
 */
export function renderWorkflowAverageValueTrendChart({
  workflow,
  trendData,
  elChartId,
  xStart,
  compareTo,
}) {
  if (!workflow || !trendData) return null;

  const elChart = document.getElementById(elChartId);
  if (!elChart) return null;
  elChart.innerHTML = null;

  const benchmark = compareTo;
  const benchmarkValues = [workflow.benchmark_50, workflow.benchmark_90].filter(
    (v) => v !== null && v !== undefined,
  );
  const minVal = Math.min(
    ...trendData.values.map((v) => v.value).concat(benchmarkValues),
  );
  const maxVal = Math.max(
    ...trendData.values.map((v) => v.value).concat(benchmarkValues),
  );
  const xEnd = trendData.values[trendData.values.length - 1].date;

  // Capture the lower_value_is_better flag at chart creation time when data is available
  const lowerValueIsBetter = trendData.workflow?.lower_value_is_better || false;

  const attrs = {
    elChart: elChart,
    startDate: xStart,
    endDate: xEnd,
    tooltipHtml(d, cd) {
      const params = {
        heading: workflow.name,
        d: {
          label: d.label,
          value: d.value,
          formattedValue: parseFloat(d.value).toFixed(2),
        },
      };

      if (cd) {
        params.cd = {
          label: cd.label,
          value: cd.value,
          formattedValue: parseFloat(cd.value).toFixed(2),
        };
      }

      // Use the captured lowerIsBetter flag
      params.lowerIsBetter = lowerValueIsBetter;

      return chartTooltipHtml.workflowAverage(params);
    },
    yAxisMin: minVal * 0.8,
    yAxisMax: maxVal * 1.2,
    showXAxisTicks: false,
    values: trendData.values,
  };

  if (
    benchmark &&
    workflow[workflowDiffOptionsMap.get(benchmark)] &&
    benchmark !== "vs_prev"
  ) {
    attrs.comparisonValues = generateComparisonValues(
      trendData,
      workflowDiffOptionsMap.get(benchmark),
      "workflow",
    );
  }

  if (benchmark == "vs_org") {
    const orgComparisonValues = [];
    const orgScore = Math.round(workflow?.org_score);
    trendData.values.forEach((value) => {
      orgComparisonValues.push({
        date: value.date,
        label: "vs Org",
        value: orgScore,
      });
    });
    attrs.comparisonValues = orgComparisonValues;
  }

  return createLineChartWithBenchmark(attrs);
}

/**
 * Render workflow score (% in target range) trend chart
 * @param {Object} params - Chart parameters
 * @param {Object} params.workflow - Workflow data
 * @param {Object} params.trendData - Trend data with values array
 * @param {string} params.elChartId - DOM element ID for chart container
 * @param {string} params.xStart - Start date for x-axis
 * @param {string} params.compareTo - Comparison benchmark type
 * @returns {Object|null} LineChart instance or null if prerequisites missing
 */
export function renderWorkflowPercentInTargetRangeTrendChart({
  workflow,
  trendData,
  elChartId,
  xStart,
  compareTo,
}) {
  if (!workflow || !trendData) return null;

  const elChart = document.getElementById(elChartId);
  if (!elChart) return null;
  elChart.innerHTML = null;

  const benchmark = compareTo;
  // For score charts, benchmarks are percentages (0-100)
  const benchmarkValues = [
    workflow.benchmark_50_percentage,
    workflow.benchmark_90_percentage,
  ].filter((v) => v !== null && v !== undefined);

  const scoreValues = trendData.values
    .map((v) => v.score)
    .filter((v) => v !== null && v !== undefined);

  const minVal = Math.min(...scoreValues.concat(benchmarkValues));
  const maxVal = Math.max(...scoreValues.concat(benchmarkValues));
  const xEnd = trendData.values[trendData.values.length - 1].date;

  const attrs = {
    elChart: elChart,
    startDate: xStart,
    endDate: xEnd,
    tooltipHtml(d, cd) {
      const params = {
        heading: "% in target range",
        d: {
          label: d.label,
          value: d.value,
          formattedValue: Math.round(d.value) + "%",
        },
      };

      if (cd) {
        params.cd = {
          label: cd.label,
          value: cd.value,
          formattedValue: Math.round(cd.value) + "%",
        };
      }

      return chartTooltipHtml.default(params);
    },
    yAxisMin: Math.max(0, minVal * 0.8),
    yAxisMax: Math.min(100, maxVal * 1.2),
    showXAxisTicks: false,
    values: trendData.values.map((v) => ({
      date: v.date,
      label: v.label,
      value: v.score,
    })),
  };

  // Add industry benchmark comparison lines (P50, P75, P90)
  if (
    benchmark &&
    benchmark !== "vs_org" &&
    benchmark !== "vs_prev" &&
    workflow[workflowDiffOptionsMap.get(benchmark) + "_percentage"] !== null &&
    workflow[workflowDiffOptionsMap.get(benchmark) + "_percentage"] !==
      undefined
  ) {
    // Create trend data with percentage benchmarks for generateComparisonValues
    const trendDataWithPercentageBenchmarks = {
      values: trendData.values,
      workflow: {
        benchmark_50: workflow.benchmark_50_percentage,
        benchmark_75: workflow.benchmark_75_percentage,
        benchmark_90: workflow.benchmark_90_percentage,
      },
    };

    attrs.comparisonValues = generateComparisonValues(
      trendDataWithPercentageBenchmarks,
      workflowDiffOptionsMap.get(benchmark),
      "workflow",
    );
  }

  if (benchmark == "vs_org" && workflow?.org_percentage !== null) {
    const orgComparisonValues = [];
    const orgScore = Math.round(workflow?.org_percentage);
    trendData.values.forEach((value) => {
      orgComparisonValues.push({
        date: value.date,
        label: "vs Org",
        value: orgScore,
      });
    });
    attrs.comparisonValues = orgComparisonValues;
  }

  return createLineChartWithBenchmark(attrs);
}

/**
 * Render workflow annualized cost trend chart
 * @param {Object} params - Chart parameters
 * @param {Object} params.workflow - Workflow data
 * @param {Object} params.trendData - Trend data with values array
 * @param {string} params.elChartId - DOM element ID for chart container
 * @param {string} params.xStart - Start date for x-axis
 * @param {string} params.compareTo - Comparison benchmark type
 * @returns {Object|null} LineChart instance or null if prerequisites missing
 */
export function renderWorkflowAnnualizedCostTrendChart({
  workflow,
  trendData,
  elChartId,
  xStart,
  // compareTo,
}) {
  if (!workflow || !trendData) return null;

  const elChart = document.getElementById(elChartId);
  if (!elChart) return null;
  elChart.innerHTML = null;

  // const benchmark = compareTo;
  // const WORK_WEEKS_PER_YEAR = 48;

  // Extract cost values from trend data
  const costValues = trendData.values.map((v) => ({
    date: v.date,
    label: v.label,
    value: v.annualized_time_cost_per_engineer || 0,
  }));

  // Temporarily disabled: Don't include benchmark values in y-axis scale calculation
  /*
  // Calculate annualized benchmark costs from average benchmarks
  const annualizedBenchmarkValues = [
    workflow.benchmark_50 ? workflow.benchmark_50 * WORK_WEEKS_PER_YEAR : null,
    workflow.benchmark_90 ? workflow.benchmark_90 * WORK_WEEKS_PER_YEAR : null,
  ].filter((v) => v !== null && v !== undefined);

  // Calculate min/max for y-axis scaling, including benchmarks
  const minVal = Math.min(
    ...costValues.map((v) => v.value).concat(annualizedBenchmarkValues),
  );
  const maxVal = Math.max(
    ...costValues.map((v) => v.value).concat(annualizedBenchmarkValues),
  );
  */

  // Calculate min/max for y-axis scaling from data only
  const minVal = Math.min(...costValues.map((v) => v.value));
  const maxVal = Math.max(...costValues.map((v) => v.value));
  const xEnd = trendData.values[trendData.values.length - 1].date;

  const attrs = {
    elChart: elChart,
    startDate: xStart,
    endDate: xEnd,
    tooltipHtml(d, cd) {
      const params = {
        heading: "Annualized cost",
        d: {
          label: d.label,
          value: d.value,
          formattedValue: Math.round(d.value) + " hrs",
        },
      };

      if (cd) {
        params.cd = {
          label: cd.label,
          value: cd.value,
          formattedValue: Math.round(cd.value) + " hrs",
        };
      }

      return chartTooltipHtml.timeLoss(params);
    },
    yAxisMin: minVal * 0.8,
    yAxisMax: maxVal * 1.2,
    showXAxisTicks: false,
    values: costValues,
  };

  // Temporarily disabled: Industry benchmarks for annualized cost don't account for PR merge frequency and other multipliers
  // This creates invalid comparisons (e.g., -76% when team is actually performing well)
  // TODO: Implement proper annualized cost benchmarks that include PR merge frequency in the calculation
  /*
  // Add comparison line for benchmarks
  if (
    benchmark &&
    ((workflow[workflowDiffOptionsMap.get(benchmark)] !== null &&
      workflow[workflowDiffOptionsMap.get(benchmark)] !== undefined) ||
      benchmark === "vs_prev")
  ) {
    // Create annualized cost trend data with benchmark values
    const trendDataWithAnnualizedBenchmark = {
      values: trendData.values,
      workflow: {
        benchmark_50: workflow.benchmark_50
          ? Math.round(workflow.benchmark_50 * WORK_WEEKS_PER_YEAR)
          : null,
        benchmark_75: workflow.benchmark_75
          ? Math.round(workflow.benchmark_75 * WORK_WEEKS_PER_YEAR)
          : null,
        benchmark_90: workflow.benchmark_90
          ? Math.round(workflow.benchmark_90 * WORK_WEEKS_PER_YEAR)
          : null,
      },
    };

    attrs.comparisonValues = generateComparisonValues(
      trendDataWithAnnualizedBenchmark,
      workflowDiffOptionsMap.get(benchmark),
      "workflow",
    );
  }

  if (benchmark == "vs_org") {
    // Calculate annualized org cost from org avg_value
    const annualizedOrgCost = workflow?.org_avg_value
      ? Math.round(workflow.org_avg_value * WORK_WEEKS_PER_YEAR)
      : null;

    if (annualizedOrgCost) {
      const orgComparisonValues = [];
      trendData.values.forEach((value) => {
        orgComparisonValues.push({
          date: value.date,
          label: "vs Org",
          value: annualizedOrgCost,
        });
      });
      attrs.comparisonValues = orgComparisonValues;
    }
  }
  */

  return createLineChartWithBenchmark(attrs);
}

/**
 * Render workflow distribution stacked area chart
 * @param {Object} params - Chart parameters
 * @param {Object} params.workflow - Workflow data
 * @param {Object} params.trendData - Trend data with values array
 * @param {string} params.elChartId - DOM element ID for chart container
 * @param {string} params.xStart - Start date for x-axis
 * @param {string} params.compareTo - Comparison benchmark type
 * @param {boolean} params.hasMultipleTrendValues - Whether there are multiple trend values
 * @returns {Object|null} StackedAreaChart instance or null if prerequisites missing
 */
export function renderWorkflowDistributionTrendChart({
  workflow,
  trendData,
  elChartId,
  xStart,
  compareTo,
  hasMultipleTrendValues,
}) {
  if (!workflow || !trendData || !hasMultipleTrendValues) return null;

  let elChart = document.getElementById(elChartId);
  if (!elChart) return null;
  elChart.innerHTML = null;

  const benchmark = compareTo;
  const benchmarkValues = [workflow.benchmark_50, workflow.benchmark_90].filter(
    (v) => v !== null && v !== undefined,
  );
  const minVal = Math.min(
    ...trendData.values.map((v) => v.value).concat(benchmarkValues),
  );
  const maxVal = Math.max(
    ...trendData.values.map((v) => v.value).concat(benchmarkValues),
  );
  const xEnd = trendData.values[trendData.values.length - 1].date;

  let stackedAreaValues = { series: [], dates: [] };
  let options = JSON.parse(trendData.values[0]?.options);
  let needsReversing = workflow?.target_direction === "d";
  if (needsReversing) options = options.reverse();

  const colors = getDistributionColors(options.length);

  options.forEach((opt, idx) => {
    let indexToUse = idx;
    // Go backwards through the list of options to ensure ordering matches the distribution chart above
    if (needsReversing) indexToUse = options.length - (idx + 1);

    stackedAreaValues.series.push({
      name: opt,
      color: colors[idx % colors.length],
      counts: [],
      key: `${numberToSpelling(indexToUse)}_position_count`,
    });
  });

  // Formatting the trend data to match the required format the stacked area chart needs
  trendData.values.forEach((val) => {
    stackedAreaValues.dates.push(val.date);

    stackedAreaValues.series.forEach((breakdownValue) => {
      breakdownValue.counts.push(val.breakdown[breakdownValue.key]);
    });
  });

  // NOTE: reversing here so that the stacks matches the order of the distribution chart above
  stackedAreaValues.series = stackedAreaValues.series.reverse();

  const attrs = {
    elChart: elChart,
    startDate: xStart,
    endDate: xEnd,
    tooltipHtml(d, cd) {
      const params = {
        heading: new Intl.DateTimeFormat("en-US", {
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        }).format(d),
        rows: cd
          .map((r) => ({
            label: r.name,
            value: r.count,
            formattedValue: Math.round(r.percentage * 100) + "%",
            color: r.color,
          }))
          .reverse(),
      };

      return chartTooltipHtml.workflowAverage(params);
    },
    yAxisMin: minVal * 0.8,
    yAxisMax: maxVal * 1.2,
    showXAxisTicks: false,
    values: stackedAreaValues,
  };

  if (
    benchmark &&
    workflow[workflowDiffOptionsMap.get(benchmark)] &&
    benchmark !== "vs_prev"
  ) {
    attrs.comparisonValues = generateComparisonValues(
      trendData,
      workflowDiffOptionsMap.get(benchmark),
      "workflow",
    );
  }

  return new StackedAreaChart(attrs);
}

/**
 * Render driver distribution trend chart
 * @param {Object} params - Chart parameters
 * @param {Object} params.driver - Driver data with scale information
 * @param {Object} params.trendData - Trend data with values array containing breakdown
 * @param {string} params.elChartId - DOM element ID for chart container
 * @param {string} params.xStart - Start date for x-axis
 * @param {boolean} params.hasMultipleTrendValues - Whether there are multiple trend values
 * @returns {Object|null} StackedAreaChart instance or null if prerequisites missing
 */
export function renderDriverDistributionTrendChart({
  driver,
  trendData,
  elChartId,
  xStart,
  hasMultipleTrendValues,
}) {
  let elChart = document.getElementById(elChartId);
  if (!elChart) return null;
  elChart.innerHTML = null;

  if (!driver || !trendData || !hasMultipleTrendValues) return null;

  const benchmarkValues = [driver.benchmark_50, driver.benchmark_90].filter(
    (v) => v !== null && v !== undefined,
  );
  const minVal = Math.min(
    ...trendData.values.map((v) => v.value).concat(benchmarkValues),
  );
  const maxVal = Math.max(
    ...trendData.values.map((v) => v.value).concat(benchmarkValues),
  );
  const xEnd = trendData.values[trendData.values.length - 1].date;

  // Driver distribution uses values 1-5 for 5-point scale or 1-3 for 3-point scale
  const scalePoints = driver.scale_points || 5;
  const colors = getDistributionColors(scalePoints);

  let stackedAreaValues = { series: [], dates: [] };

  // Build series for each scale point
  const positions =
    scalePoints === 5
      ? ["five", "four", "three", "two", "one"]
      : ["three", "two", "one"];
  const labels =
    scalePoints === 5
      ? ["Always", "Very often", "Sometimes", "Rarely", "Never"]
      : ["Sometimes", "Rarely", "Never"];

  positions.forEach((position, idx) => {
    stackedAreaValues.series.push({
      name: labels[idx],
      color: colors[idx],
      counts: [],
      key: `${position}_count`,
    });
  });

  // Format the trend data to match the required format for stacked area chart
  trendData.values.forEach((val) => {
    stackedAreaValues.dates.push(val.date);

    stackedAreaValues.series.forEach((breakdownValue) => {
      breakdownValue.counts.push(val.breakdown[breakdownValue.key] || 0);
    });
  });

  // Reverse so that the stacks match the order of the distribution chart
  stackedAreaValues.series = stackedAreaValues.series.reverse();

  const attrs = {
    elChart: elChart,
    startDate: xStart,
    endDate: xEnd,
    tooltipHtml(d, cd) {
      const params = {
        heading: new Intl.DateTimeFormat("en-US", {
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        }).format(d),
        rows: cd
          .map((r) => ({
            label: r.name,
            value: r.count,
            formattedValue: Math.round(r.percentage * 100) + "%",
            color: r.color,
          }))
          .reverse(),
      };

      return chartTooltipHtml.workflowAverage(params);
    },
    yAxisMin: minVal * 0.8,
    yAxisMax: maxVal * 1.2,
    showXAxisTicks: false,
    values: stackedAreaValues,
  };

  return new StackedAreaChart(attrs);
}

/**
 * Render driver (factor) trend chart
 * @param {Object} params - Chart parameters
 * @param {Object} params.trends - Driver trends data
 * @param {string} params.elChartId - DOM element ID for chart container
 * @param {string} params.xStart - Start date for x-axis
 * @param {string} params.compareTo - Comparison benchmark type
 * @param {number} params.orgScore - Organization score for comparison
 * @returns {Object|null} LineChart instance or null if prerequisites missing
 */
export function renderDriverTrendChart({
  trends,
  elChartId,
  xStart,
  compareTo,
  orgScore,
}) {
  const elChart = document.getElementById(elChartId);
  if (!elChart) return null;

  elChart.innerHTML = null;
  const benchmark = compareTo;
  const benchmarkValues = [
    trends.factor.benchmark_50,
    trends.factor.benchmark_90,
  ].filter((v) => v !== null && v !== undefined);
  const minVal = Math.min(
    ...trends.values.map((v) => v.value).concat(benchmarkValues),
  );
  const maxVal = Math.max(
    ...trends.values.map((v) => v.value).concat(benchmarkValues),
  );

  const trendSize = trends.values.length;
  // Avoid using 'this' in passing in function for the tooltip callback, get the string explicitly
  const heading = trends.factor.name;

  const attrs = {
    elChart: elChart,
    startDate: xStart,
    endDate: trends.values[trendSize - 1]?.date,
    tooltipHtml(d, cd) {
      const params = {
        heading: heading,
        d: {
          label: d.label,
          value: d.value,
          formattedValue: parseFloat(d.value).toFixed(2),
        },
      };

      if (cd) {
        params.cd = {
          label: cd.label,
          value: cd.value,
          formattedValue: parseFloat(cd.value).toFixed(2),
        };
      }

      return chartTooltipHtml.default(params);
    },
    yAxisMin: minVal * 0.8,
    yAxisMax: maxVal * 1.2,
    showXAxisTicks: false,
    values: trends.values,
  };

  if (
    benchmark &&
    (trends.factor[workflowDiffOptionsMap.get(benchmark)] ||
      benchmark === "vs_prev")
  ) {
    attrs.comparisonValues = generateComparisonValues(
      trends,
      workflowDiffOptionsMap.get(benchmark),
      "factor",
    );
  }

  if (benchmark == "vs_org") {
    const orgComparisonValues = [];
    trends.values.forEach((value) => {
      orgComparisonValues.push({
        date: value.date,
        label: "vs Org",
        value: Math.round(orgScore),
      });
    });
    attrs.comparisonValues = orgComparisonValues;
  }

  return new LineChart(attrs);
}
